import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { methodologyService, SubMethodologyContent, ContentItem, SubMethodology, ContentEntry } from "../../lib/firebase";

interface ContentEntryFormData {
  id: string; // Unique ID for this content entry
  name: string;
  definition: string;
  keyPoints: ContentItem[];
  commonBugs: ContentItem[];
  pentestingTips: ContentItem[];
  tools: ContentItem[];
  extra: string;
}

interface SubMethodologyContentPageParams {
  methodologyId: string;
  subMethodologyId: string;
}

export const SubMethodologyContentPage = (): JSX.Element => {
  const { methodologyId, subMethodologyId } = useParams<keyof SubMethodologyContentPageParams>() as SubMethodologyContentPageParams;
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subMethodology, setSubMethodology] = useState<SubMethodology | null>(null);
  const [contentEntries, setContentEntries] = useState<ContentEntryFormData[]>([]);
  const [editingEntryIndex, setEditingEntryIndex] = useState<number | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  
  const [formData, setFormData] = useState<ContentEntryFormData>({
    id: `entry-${Date.now()}`,
    name: '',
    definition: '',
    keyPoints: [{ id: `new-${Date.now()}`, text: '' }],
    commonBugs: [{ id: `new-${Date.now()}`, text: '' }],
    pentestingTips: [{ id: `new-${Date.now()}`, text: '' }],
    tools: [{ id: `new-${Date.now()}`, text: '' }],
    extra: ''
  });

  // Convert any array type (string[] or ContentItem[]) to ContentItem[]
  const normalizeContentArray = (array: any[] | undefined): ContentItem[] => {
    if (!array || array.length === 0) return [{ id: `new-${Date.now()}`, text: '' }];
    
    return array.map((item, index) => {
      if (typeof item === 'string') {
        // Convert string to ContentItem
        return { id: `item-${index}-${Date.now()}`, text: item };
      } else if (typeof item === 'object' && item.text) {
        // It's already a ContentItem, ensure it has an id
        return { ...item, id: item.id || `item-${index}-${Date.now()}` };
      }
      // Fallback for any other case
      return { id: `item-${index}-${Date.now()}`, text: '' };
    });
  };

  // Load sub-methodology data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // First get the methodology to get the sub-methodology title
        const methodology = await methodologyService.getMethodologyById(methodologyId);
        if (!methodology) {
          setError("Methodology not found");
          setLoading(false);
          return;
        }
        
        const foundSubMethodology = methodology.subMethodologies.find(
          (sm) => sm.id === subMethodologyId
        );
        
        if (!foundSubMethodology) {
          setError("Sub-methodology not found");
          setLoading(false);
          return;
        }
        
        setSubMethodology(foundSubMethodology);
        
        // Get all entries using the new method
        console.log("Loading all content entries...");
        const allEntries = await methodologyService.getAllSubMethodologyEntries(methodologyId, subMethodologyId);
        console.log("getAllSubMethodologyEntries returned:", allEntries.length, "entries");
        
        if (allEntries.length > 0) {
          // Convert the content entries to our form data format
          const formEntries = allEntries.map(entry => ({
            id: entry.id,
            name: entry.name || '',
            definition: entry.definition || '',
            keyPoints: normalizeContentArray(entry.keyPoints),
            commonBugs: normalizeContentArray(entry.commonBugs),
            pentestingTips: normalizeContentArray(entry.pentestingTips),
            tools: normalizeContentArray(entry.tools),
            extra: entry.extra || ''
          }));
          
          setContentEntries(formEntries);
          console.log("Loaded entries from getAllSubMethodologyEntries:", formEntries.length);
          setLoading(false);
          return;
        }
        
        // If no entries found using the new method, try the old methods
        
        // Check if we have multiContent entries first
        if (foundSubMethodology.multiContent && 
            foundSubMethodology.multiContent.entries && 
            foundSubMethodology.multiContent.entries.length > 0) {
          
          console.log("Found multiContent entries:", foundSubMethodology.multiContent.entries.length);
          
          // Convert the entries to our form data format
          const entriesFromMultiContent = foundSubMethodology.multiContent.entries.map(entry => ({
            id: entry.id || `entry-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
            name: entry.name || '',
            definition: entry.definition || '',
            keyPoints: normalizeContentArray(entry.keyPoints),
            commonBugs: normalizeContentArray(entry.commonBugs),
            pentestingTips: normalizeContentArray(entry.pentestingTips),
            tools: normalizeContentArray(entry.tools),
            extra: entry.extra || ''
          }));
          
          setContentEntries(entriesFromMultiContent);
          console.log("Loaded entries from multiContent:", entriesFromMultiContent.length);
          setLoading(false);
          return;
        }
        
        // Now directly get the content using the more reliable method
        console.log("No multiContent found, fetching content directly...");
        const content = await methodologyService.getSubMethodologyContent(methodologyId, subMethodologyId);
        console.log("Direct content fetch result:", content);
        
        // Check if we have any existing content entries
        const initialEntries: ContentEntryFormData[] = [];
        
        if (content && (
          content.name || content.definition || 
          (content.keyPoints && content.keyPoints.length > 0) ||
          (content.commonBugs && content.commonBugs.length > 0) ||
          (content.pentestingTips && content.pentestingTips.length > 0) ||
          (content.tools && content.tools.length > 0) ||
          content.extra
        )) {
          console.log("Adding content to entries list");
          initialEntries.push({
            id: `entry-${Date.now()}`,
            name: content.name || foundSubMethodology.title || '',
            definition: content.definition || '',
            keyPoints: normalizeContentArray(content.keyPoints),
            commonBugs: normalizeContentArray(content.commonBugs),
            pentestingTips: normalizeContentArray(content.pentestingTips),
            tools: normalizeContentArray(content.tools),
            extra: content.extra || ''
          });
        } else if (foundSubMethodology.detailedContent) {
          // Fallback to the content from the sub-methodology object
          console.log("Falling back to content from sub-methodology");
          const fallbackContent = foundSubMethodology.detailedContent;
          
          initialEntries.push({
            id: `entry-${Date.now()}`,
            name: fallbackContent.name || foundSubMethodology.title || '',
            definition: fallbackContent.definition || '',
            keyPoints: normalizeContentArray(fallbackContent.keyPoints),
            commonBugs: normalizeContentArray(fallbackContent.commonBugs),
            pentestingTips: normalizeContentArray(fallbackContent.pentestingTips),
            tools: normalizeContentArray(fallbackContent.tools),
            extra: fallbackContent.extra || ''
          });
        }
        
        setContentEntries(initialEntries);
        console.log("Loaded entries:", initialEntries.length);
        
      } catch (error) {
        console.error("Error loading sub-methodology data:", error);
        setError("Failed to load sub-methodology data");
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [methodologyId, subMethodologyId]);

  const resetForm = () => {
    setFormData({
      id: `entry-${Date.now()}`,
      name: '',
      definition: '',
      keyPoints: [{ id: `new-${Date.now()}`, text: '' }],
      commonBugs: [{ id: `new-${Date.now()}`, text: '' }],
      pentestingTips: [{ id: `new-${Date.now()}`, text: '' }],
      tools: [{ id: `new-${Date.now()}`, text: '' }],
      extra: ''
    });
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    resetForm();
    window.scrollTo(0, 0);
  };

  const handleEdit = (index: number) => {
    setEditingEntryIndex(index);
    setFormData(contentEntries[index]);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (index: number) => {
    if (!window.confirm("Are you sure you want to delete this content entry?")) {
      return;
    }
    
    try {
      const newEntries = [...contentEntries];
      newEntries.splice(index, 1);
      setContentEntries(newEntries);
      
      // TODO: Implement proper saving of multiple entries when the backend supports it
      // For now, we're saving the first entry as the main content
      await saveToBackend(newEntries);
      
    } catch (error) {
      console.error("Error deleting content entry:", error);
      setError("Failed to delete content entry");
    }
  };

  const handleCancelEdit = () => {
    setEditingEntryIndex(null);
    setIsAddingNew(false);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Clean up empty array items
      const cleanedFormData = {
        ...formData,
        keyPoints: formData.keyPoints.filter(item => item.text.trim() !== ''),
        commonBugs: formData.commonBugs.filter(item => item.text.trim() !== ''),
        pentestingTips: formData.pentestingTips.filter(item => item.text.trim() !== ''),
        tools: formData.tools.filter(item => item.text.trim() !== '')
      };
      
      const newEntries = [...contentEntries];
      
      if (isAddingNew) {
        newEntries.push(cleanedFormData);
      } else if (editingEntryIndex !== null) {
        newEntries[editingEntryIndex] = cleanedFormData;
      }
      
      setContentEntries(newEntries);
      
      // Save to backend
      await saveToBackend(newEntries);
      
      // Reset form and state
      setIsAddingNew(false);
      setEditingEntryIndex(null);
      resetForm();
      
    } catch (error) {
      console.error("Error saving content entry:", error);
      setError("Failed to save content entry");
    }
  };

  // For now, just save the first entry as the main content until backend supports multiple entries
  const saveToBackend = async (entries: ContentEntryFormData[]) => {
    console.log("Saving entries to backend:", entries.length);
    
    try {
      if (entries.length === 0) {
        // If no entries, save an empty object
        const success = await methodologyService.setSubMethodologyContent(
          methodologyId,
          subMethodologyId,
          {}
        );
        
        if (!success) {
          console.error("Failed to save empty content");
          throw new Error("Failed to save empty content");
        }
        return;
      }
      
      // Create ContentEntry array for all entries
      const contentEntries: ContentEntry[] = entries.map(entry => ({
        id: entry.id,
        name: entry.name,
        definition: entry.definition,
        keyPoints: entry.keyPoints.filter(item => item.text.trim() !== '').map(item => item.text),
        commonBugs: entry.commonBugs.filter(item => item.text.trim() !== '').map(item => item.text),
        pentestingTips: entry.pentestingTips.filter(item => item.text.trim() !== '').map(item => item.text),
        tools: entry.tools.filter(item => item.text.trim() !== '').map(item => item.text),
        extra: entry.extra,
        completedItems: [] // Initialize empty completed items array
      }));
      
      // First try the multi-content method for proper multi-entry support
      console.log("Saving using updateSubMethodologyMultiContent with", contentEntries.length, "entries");
      const multiSuccess = await methodologyService.updateSubMethodologyMultiContent(
        methodologyId,
        subMethodologyId,
        contentEntries
      );
      
      if (multiSuccess) {
        console.log("Multi-content entries saved successfully");
        
        // Also save the first entry as detailedContent to ensure consistency
        // This is a redundant save, but ensures both methods of access have the data
        if (contentEntries.length > 0) {
          const firstEntry = contentEntries[0];
          console.log("Also saving first entry as detailedContent for compatibility");
          
          // Use the direct method for reliable saving
          await methodologyService.setSubMethodologyContent(
            methodologyId,
            subMethodologyId,
            firstEntry
          );
        }
        
        return;
      }
      
      // If multi-content method fails, fall back to the traditional method with the first entry
      console.log("Multi-content method failed, using fallback method with first entry");
      const firstEntry = entries[0];
      const contentToSave: SubMethodologyContent = {
        name: firstEntry.name,
        definition: firstEntry.definition,
        keyPoints: firstEntry.keyPoints.filter(item => item.text.trim() !== '').map(item => item.text),
        commonBugs: firstEntry.commonBugs.filter(item => item.text.trim() !== '').map(item => item.text),
        pentestingTips: firstEntry.pentestingTips.filter(item => item.text.trim() !== '').map(item => item.text),
        tools: firstEntry.tools.filter(item => item.text.trim() !== '').map(item => item.text),
        extra: firstEntry.extra,
        completedItems: []
      };
      
      console.log("Content to save:", contentToSave);
      
      // Use the direct method for reliable saving
      const success = await methodologyService.setSubMethodologyContent(
        methodologyId,
        subMethodologyId,
        contentToSave
      );
      
      if (!success) {
        throw new Error("Failed to save content using both methods");
      }
      
      console.log("Content saved successfully using fallback method");
    } catch (error) {
      console.error("Error in saveToBackend:", error);
      throw error;
    }
  };

  const handleArrayInputChange = (
    field: 'keyPoints' | 'commonBugs' | 'pentestingTips' | 'tools',
    index: number,
    value: string
  ) => {
    setFormData(prev => {
      const currentArray = prev[field];
      const newArray = [...currentArray];
      newArray[index] = { ...newArray[index], text: value };
      
      // If editing the last item and it's not empty, add a new empty item
      if (index === newArray.length - 1 && value.trim() !== '') {
        newArray.push({ id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, text: '' });
      }
      
      return {
        ...prev,
        [field]: newArray
      };
    });
  };

  const handleRemoveArrayItem = (
    field: 'keyPoints' | 'commonBugs' | 'pentestingTips' | 'tools',
    index: number
  ) => {
    setFormData(prev => {
      const currentArray = prev[field];
      const newArray = [...currentArray];
      
      if (newArray.length > 1) {
        newArray.splice(index, 1);
      } else {
        newArray[0] = { id: `new-${Date.now()}`, text: '' };
      }
      
      return {
        ...prev,
        [field]: newArray
      };
    });
  };

  const renderArrayInputs = (
    field: 'keyPoints' | 'commonBugs' | 'pentestingTips' | 'tools',
    label: string
  ) => {
    const fieldValues = formData[field] || [{ id: `new-${Date.now()}`, text: '' }];
    
    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-black mb-1">
          {label}
        </label>
        {fieldValues.map((item, index) => (
          <div key={item.id} className="flex gap-2">
            <textarea
              value={item.text}
              onChange={(e) => handleArrayInputChange(field, index, e.target.value)}
              className="h-24 bg-white rounded-lg text-black placeholder-[#00000080] px-4 py-2 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 border border-[#E7E7E7] flex-1"
              placeholder={`Enter ${label.toLowerCase()}`}
            />
            {(fieldValues.length > 1 || index < fieldValues.length - 1) && (
              <button
                type="button"
                onClick={() => handleRemoveArrayItem(field, index)}
                className="h-10 w-10 flex items-center justify-center text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75]">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => navigate('/aAyuiOcvMq/methodologies')}
                className="text-black opacity-60 hover:opacity-100 transition-opacity"
              >
                ← Back to Methodologies
              </button>
            </div>
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              {subMethodology?.title || "Sub-methodology Content"}
            </h1>
            <p className="text-black opacity-70">
              Manage content entries for this sub-methodology
            </p>
          </div>
          {!isAddingNew && editingEntryIndex === null && (
            <Button
              className="h-10 px-4 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
              onClick={handleAddNew}
            >
              Add New Content
            </Button>
          )}
        </div>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        )}
        
        {loading ? (
          <div className="bg-white rounded-[20px] p-6 shadow-lg flex items-center justify-center h-64">
            <p>Loading content...</p>
          </div>
        ) : (
          <>
            {/* Form for adding/editing content */}
            {(isAddingNew || editingEntryIndex !== null) && (
              <div className="bg-white rounded-[20px] p-6 shadow-lg mb-8">
                <h2 className="font-['Space_Grotesk'] font-bold text-xl text-black mb-4">
                  {isAddingNew ? "Add New Content" : "Edit Content"}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Name field */}
                  <div>
                    <label className="block text-sm font-medium text-black mb-1">
                      Name
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="h-10 bg-white rounded-lg text-black placeholder-[#00000080] px-4 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 border border-[#E7E7E7]"
                      placeholder="Enter name (e.g., Broken Access Control)"
                      required
                    />
                  </div>
                  
                  {/* Definition field */}
                  <div>
                    <label className="block text-sm font-medium text-black mb-1">
                      Definition
                    </label>
                    <textarea
                      value={formData.definition}
                      onChange={(e) => setFormData(prev => ({ ...prev, definition: e.target.value }))}
                      className="w-full min-h-[80px] bg-white rounded-lg text-black placeholder-[#00000080] px-4 py-2 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 border border-[#E7E7E7]"
                      placeholder="Enter definition"
                    />
                  </div>
                  
                  {/* Key Points fields */}
                  {renderArrayInputs('keyPoints', 'Key Points')}
                  
                  {/* Common Bugs fields */}
                  {renderArrayInputs('commonBugs', 'Common Bugs')}
                  
                  {/* Pentesting Tips fields */}
                  {renderArrayInputs('pentestingTips', 'Pentesting Tips')}
                  
                  {/* Tools fields */}
                  {renderArrayInputs('tools', 'Tools')}
                  
                  {/* Extra field */}
                  <div>
                    <label className="block text-sm font-medium text-black mb-1">
                      Additional Notes (Optional)
                    </label>
                    <textarea
                      value={formData.extra}
                      onChange={(e) => setFormData(prev => ({ ...prev, extra: e.target.value }))}
                      className="w-full min-h-[80px] bg-white rounded-lg text-black placeholder-[#00000080] px-4 py-2 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 border border-[#E7E7E7]"
                      placeholder="Enter any additional information"
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button
                      type="button"
                      onClick={handleCancelEdit}
                      className="h-9 px-4 bg-gray-100 text-black rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="h-9 px-4 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
                    >
                      {isAddingNew ? 'Add Content' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              </div>
            )}
            
            {/* List of content entries */}
            <div className="bg-white rounded-[20px] p-6 shadow-lg">
              <h2 className="font-['Space_Grotesk'] font-bold text-xl text-black mb-4">
                Content Entries
              </h2>
              
              {contentEntries.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No content entries yet. Click "Add New Content" to create one.
                </div>
              ) : (
                <div className="space-y-6">
                  {contentEntries.map((entry, index) => (
                    <div key={entry.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black">
                          {entry.name}
                        </h3>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleEdit(index)}
                            className="text-sm text-black opacity-60 hover:opacity-100 transition-opacity"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(index)}
                            className="text-sm text-red-600 opacity-60 hover:opacity-100 transition-opacity"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      
                      {entry.definition && (
                        <div className="mb-3">
                          <h4 className="text-sm font-medium text-black mb-1">Definition</h4>
                          <p className="text-sm text-black opacity-80">{entry.definition}</p>
                        </div>
                      )}
                      
                      {entry.keyPoints.length > 0 && entry.keyPoints[0].text.trim() !== '' && (
                        <div className="mb-3">
                          <h4 className="text-sm font-medium text-black mb-1">Key Points</h4>
                          <ul className="list-disc pl-5 text-sm text-black opacity-80">
                            {entry.keyPoints.map((item, i) => (
                              item.text.trim() !== '' && (
                                <li key={i}>{item.text}</li>
                              )
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* We could show more fields here but keeping it simple for the preview */}
                      <div className="text-xs text-black opacity-60 mt-2">
                        {`${entry.commonBugs.filter(i => i.text.trim() !== '').length} common bugs, 
                        ${entry.pentestingTips.filter(i => i.text.trim() !== '').length} pentesting tips, 
                        ${entry.tools.filter(i => i.text.trim() !== '').length} tools`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}; 